/**
 * The shape the recipient field and the rolodex picker need from a vendor.
 *
 * Structural rather than imported from the API client on purpose: it is the
 * contract between three components and their tests, and a test fixture should
 * be able to satisfy it by hand. The live `vendors` query returns rows that
 * satisfy it already.
 */
export interface VendorOption {
  id: string;
  businessName: string;
  contactName: string | null;
  primaryCategory: string | null;
  emails: { email: string; isPrimary: boolean }[];
}

/**
 * Does this vendor answer to what the planner typed?
 *
 * Emails are in scope, and that is the point of the control: the vendor a
 * planner has mailed from their own account for six years is often filed in
 * their head as an address, not a business name. The old rolodex search
 * matched `businessName` and `contactName` only, so typing a domain found
 * nobody and read as "not in your rolodex".
 *
 * An empty query matches everything — the caller decides whether an unfiltered
 * list is the right thing to show, and both callers want it.
 */
export function matchesVendorQuery(vendor: VendorOption, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  if (vendor.businessName.toLowerCase().includes(q)) return true;
  if ((vendor.contactName ?? '').toLowerCase().includes(q)) return true;
  return vendor.emails.some((e) => e.email.toLowerCase().includes(q));
}

/** The address an inquiry to this vendor would actually be sent to. */
export function primaryEmail(vendor: VendorOption): string | null {
  return (vendor.emails.find((e) => e.isPrimary) ?? vendor.emails[0])?.email ?? null;
}

/** Whether a typed string is email-shaped, deciding which field a new vendor
 *  gets prefilled into. Deliberately loose — this picks a form field, it does
 *  not validate an address. */
export function looksLikeEmail(text: string): boolean {
  return /^[^\s@]+@[^\s@]+$/.test(text.trim());
}
